// apps/admin-ui/src/components/governance/ProposalRowExpanded.tsx
// ALL RIGHTS RESERVED

/**
 * Proposal Row Expanded Component
 *
 * Expanded view showing proposal details, votes, and vote controls.
 * Migrated from apps/csr — tokens adapted to admin-ui design system.
 *
 * @module apps/admin-ui/components/governance/ProposalRowExpanded
 */

import { useState, useEffect } from 'react';
import {
  getProposal,
  castVote,
  submitProposal,
  executeProposal,
  type Proposal,
  type Vote,
  type CouncilMember,
  type CurrentMemberInfo,
} from '../../api/governance';
import { formatDateTime } from '../../utils/format';

// ═══════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════

interface ProposalRowExpandedProps {
  proposal: Proposal;
  members: CouncilMember[];
  memberInfo: CurrentMemberInfo | null;
  onRefresh: () => void;
}

type VoteChoice = 'yes' | 'no' | 'veto' | 'abstain';

// ═══════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════

function getVoteColor(vote: string): string {
  switch (vote) {
    case 'yes': return 'text-green-600';
    case 'no': return 'text-red-600';
    case 'veto': return 'text-orange-600 font-bold';
    case 'abstain': return 'text-gray-500';
    default: return 'text-gray-400';
  }
}

function getVoteButtonClass(vote: VoteChoice, selected: boolean): string {
  const base = 'px-3 py-1.5 rounded text-sm font-medium transition-colors';
  
  if (selected) {
    switch (vote) {
      case 'yes': return `${base} bg-green-600 text-white`;
      case 'no': return `${base} bg-red-600 text-white`;
      case 'veto': return `${base} bg-orange-600 text-white`;
      case 'abstain': return `${base} bg-gray-600 text-white`;
    }
  }
  
  switch (vote) {
    case 'yes': return `${base} border border-green-300 text-green-700 hover:bg-green-50`;
    case 'no': return `${base} border border-red-300 text-red-700 hover:bg-red-50`;
    case 'veto': return `${base} border border-orange-300 text-orange-700 hover:bg-orange-50`;
    case 'abstain': return `${base} border border-gray-300 text-gray-700 hover:bg-gray-50`;
  }
}

// ═══════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════

export function ProposalRowExpanded({ 
  proposal, 
  members, 
  memberInfo,
  onRefresh,
}: ProposalRowExpandedProps) {
  const [votes, setVotes] = useState<Vote[]>([]);
  const [_loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedVote, setSelectedVote] = useState<VoteChoice | null>(null);
  const [comment, setComment] = useState('');

  // Fetch votes when expanded
  useEffect(() => {
    async function fetchVotes() {
      try {
        setLoading(true);
        const data = await getProposal(proposal.id);
        // Handle both response formats: votes at top level or nested in proposal
        const votesData = data.votes || (data.proposal as any)?.votes || [];
        setVotes(votesData);
        
        // Check if current user has already voted
        if (memberInfo?.member) {
          const existingVote = votesData.find((v: Vote) => v.memberId === memberInfo.member?.id);
          if (existingVote) {
            setSelectedVote(existingVote.vote);
            setComment(existingVote.comment || '');
          }
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load votes');
      } finally {
        setLoading(false);
      }
    }
    
    fetchVotes();
  }, [proposal.id, memberInfo?.member?.id]);

  // Build vote map by member ID
  const votesByMember = new Map<string, Vote>();
  (votes || []).forEach(v => votesByMember.set(v.memberId, v));

  // Can current user vote?
  const canVote = memberInfo?.canVote && proposal.status === 'voting';
  
  // Can current user submit (for draft proposals)?
  const canSubmit = memberInfo?.canCreate && 
    proposal.status === 'draft' && 
    memberInfo.member?.id === proposal.createdBy;

  // Can current user execute?
  const canExecute = memberInfo?.canVote && 
    proposal.status === 'passed' &&
    proposal.executableAt &&
    new Date(proposal.executableAt) <= new Date();

  // Handle vote submission
  const handleVote = async () => {
    if (!selectedVote) return;
    
    try {
      setActionLoading(true);
      setError(null);
      await castVote(proposal.id, selectedVote, comment || undefined);
      onRefresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cast vote');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle proposal submission
  const handleSubmit = async () => {
    try {
      setActionLoading(true);
      setError(null);
      await submitProposal(proposal.id);
      onRefresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit proposal');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle execution
  const handleExecute = async () => {
    try {
      setActionLoading(true);
      setError(null);
      await executeProposal(proposal.id);
      onRefresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to execute proposal');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="p-4 space-y-4">
      {/* Description */}
      <div>
        <h4 className="text-sm font-medium text-content mb-2">Description</h4>
        <div className="text-sm text-content-secondary whitespace-pre-wrap bg-surface p-3 rounded border border-edge">
          {proposal.description}
        </div>
      </div>

      {/* Vote Breakdown */}
      <div>
        <h4 className="text-sm font-medium text-content mb-2">
          Votes ({votes.length} / {members.filter(m => m.role !== 'advisor').length})
        </h4>
        <div className="grid grid-cols-4 gap-2">
          {members
            .filter(m => m.role !== 'advisor')
            .map(member => {
              const vote = votesByMember.get(member.id);
              return (
                <div 
                  key={member.id}
                  className="flex items-center gap-2 text-sm p-2 bg-surface rounded border border-edge"
                >
                  <span className="truncate flex-1">{member.displayName}</span>
                  <span className={`font-medium ${vote ? getVoteColor(vote.vote) : 'text-gray-300'}`}>
                    {vote ? vote.vote : '—'}
                  </span>
                </div>
              );
            })}
        </div>
      </div>

      {/* Summary */}
      <div className="flex items-center gap-6 text-sm">
        <div>
          <span className="text-content-secondary">Yes:</span>{' '}
          <span className="text-green-600 font-medium">{proposal.yesVotes}</span>
        </div>
        <div>
          <span className="text-content-secondary">No:</span>{' '}
          <span className="text-red-600 font-medium">{proposal.noVotes}</span>
        </div>
        <div>
          <span className="text-content-secondary">Veto:</span>{' '}
          <span className="text-orange-600 font-medium">{proposal.vetoVotes}</span>
        </div>
        <div>
          <span className="text-content-secondary">Abstain:</span>{' '}
          <span className="text-gray-600 font-medium">{proposal.abstainVotes}</span>
        </div>
        <div className="text-content-muted">
          Quorum: 5 yes | Veto threshold: 3
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
          {error}
        </div>
      )}

      {/* Actions */}
      <div className="border-t border-edge pt-4">
        {/* Draft: Submit button */}
        {canSubmit && (
          <div className="flex items-center gap-4">
            <span className="text-sm text-content-secondary">This proposal is a draft.</span>
            <button
              onClick={handleSubmit}
              disabled={actionLoading}
              className="px-4 py-2 bg-brand-500 text-white rounded-md hover:bg-brand-500/90 transition-colors disabled:opacity-50"
            >
              {actionLoading ? 'Submitting...' : 'Submit for Voting'}
            </button>
          </div>
        )}

        {/* Voting: Vote controls */}
        {canVote && (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <span className="text-sm text-content-secondary mr-2">My Vote:</span>
              {(['yes', 'no', 'abstain'] as VoteChoice[]).map(vote => (
                <button
                  key={vote}
                  onClick={() => setSelectedVote(vote)}
                  className={getVoteButtonClass(vote, selectedVote === vote)}
                >
                  {vote.charAt(0).toUpperCase() + vote.slice(1)}
                </button>
              ))}
              {memberInfo?.canVeto && (
                <button
                  onClick={() => setSelectedVote('veto')}
                  className={getVoteButtonClass('veto', selectedVote === 'veto')}
                >
                  Veto
                </button>
              )}
            </div>

            <div className="flex items-center gap-3">
              <input
                type="text"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Optional comment..."
                className="flex-1 px-3 py-1.5 border border-edge rounded-md text-sm bg-surface"
              />
              <button
                onClick={handleVote}
                disabled={!selectedVote || actionLoading}
                className="px-4 py-1.5 bg-brand-500 text-white rounded-md hover:bg-brand-500/90 transition-colors disabled:opacity-50 text-sm"
              >
                {actionLoading ? 'Submitting...' : 'Submit Vote'}
              </button>
            </div>
          </div>
        )}

        {/* Passed: Execute button */}
        {canExecute && (
          <div className="flex items-center gap-4">
            <span className="text-sm text-content-secondary">
              This proposal passed and is ready for execution.
            </span>
            <button
              onClick={handleExecute}
              disabled={actionLoading}
              className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors disabled:opacity-50"
            >
              {actionLoading ? 'Executing...' : 'Mark as Executed'}
            </button>
          </div>
        )}

        {/* Passed but waiting */}
        {proposal.status === 'passed' && proposal.executableAt && new Date(proposal.executableAt) > new Date() && (
          <div className="text-sm text-content-secondary">
            This proposal passed. Executable after: {formatDateTime(proposal.executableAt)}
          </div>
        )}

        {/* View-only states */}
        {!canVote && !canSubmit && !canExecute && proposal.status === 'voting' && (
          <div className="text-sm text-content-muted">
            {memberInfo?.member?.role === 'advisor' 
              ? 'Advisors cannot vote on proposals.'
              : 'You do not have voting privileges.'}
          </div>
        )}

        {['rejected', 'vetoed', 'expired', 'executed'].includes(proposal.status) && (
          <div className="text-sm text-content-muted">
            This proposal is {proposal.status}.
            {proposal.executedAt && ` Executed: ${formatDateTime(proposal.executedAt)}`}
          </div>
        )}
      </div>

      {/* Metadata */}
      <div className="text-xs text-content-muted flex items-center gap-4 pt-2 border-t border-edge">
        <span>Created: {formatDateTime(proposal.createdAt)}</span>
        {proposal.votingStartsAt && (
          <span>Voting started: {formatDateTime(proposal.votingStartsAt)}</span>
        )}
        {proposal.votingEndsAt && (
          <span>Voting ends: {formatDateTime(proposal.votingEndsAt)}</span>
        )}
      </div>
    </div>
  );
}
